export type Strategy = {
  id: string;
  label: string;
  tagline: string;
  moves: string;
  cashCertainty: string;
  offerStrength: string;
  overlapRisk: string;
  failurePoints: string;
  bestFor: string;
};

export const STRATEGIES: Strategy[] = [
  {
    id: "sell-first",
    label: "Sell first",
    tagline: "Close the sale, then shop with cash in hand",
    moves: "Usually two — into temporary housing, then into the new home, unless you negotiate a rent-back.",
    cashCertainty: "Highest. You know your exact net proceeds before you write an offer.",
    offerStrength: "Strong. No sale contingency, and you can often shorten your financing timeline.",
    overlapRisk: "None on the carrying-cost side — you never own two homes at once.",
    failurePoints:
      "You may be renting or storing belongings while prices move. A fast sale in a slow buying market can leave you housed temporarily for longer than planned.",
    bestFor:
      "Buyers who need the equity to qualify or to fund the down payment, and buyers relocating into a competitive market where contingent offers get ignored.",
  },
  {
    id: "buy-first",
    label: "Buy first",
    tagline: "Secure the new home, then list the old one",
    moves: "One. You move directly into the new home and sell empty or staged.",
    cashCertainty:
      "Lowest. Your proceeds are an estimate until the old home closes, and the shortfall has to be bridged somehow.",
    offerStrength:
      "Strong if you can qualify without a sale contingency; weak if the offer depends on your current home selling.",
    overlapRisk:
      "Highest. Two mortgages, two sets of taxes, insurance, and utilities for however long the old home sits.",
    failurePoints:
      "Carrying both payments longer than budgeted, a price reduction on the old home, or a lender that will not count the departing residence income.",
    bestFor:
      "Buyers with enough income or reserves to carry both homes, or who are using a bridge loan, HELOC, or buy-before-you-sell service.",
  },
  {
    id: "simultaneous",
    label: "Simultaneous closings",
    tagline: "Close the sale and the purchase on the same day or back to back",
    moves: "One, in theory. Movers load in one city and unload in another with no storage in between.",
    cashCertainty:
      "Good on paper — sale proceeds fund the purchase — but the money has to arrive before the purchase can fund.",
    offerStrength:
      "Moderate. Sellers know your closing depends on another transaction, even when it is not written as a contingency.",
    overlapRisk: "Minimal carrying cost, maximum schedule risk.",
    failurePoints:
      "Any delay on the sale side cascades: a buyer's underwriting hiccup, a wire that lands after cutoff, or two states with different funding and recording rules can strand you mid-move.",
    bestFor:
      "Cross-city moves where both closings can be coordinated by cooperating agents and closers, and where you have a fallback plan for a one to three day gap.",
  },
];

export type FinancingTool = {
  id: string;
  name: string;
  how: string;
  fit: string;
  watch: string;
};

export const FINANCING_TOOLS: FinancingTool[] = [
  {
    id: "bridge-loan",
    name: "Bridge loan",
    how: "A short-term loan secured by your current home that funds the down payment on the new one and is repaid when the old home sells.",
    fit: "Buy-first moves where the equity is real but locked up, and the old home is expected to sell within a few months.",
    watch:
      "Higher rates, origination fees, and a repayment clock that keeps running if the sale is slow. Terms, availability, and qualification vary by lender and market.",
  },
  {
    id: "heloc",
    name: "HELOC on the departing home",
    how: "A revolving line drawn against existing equity, used for the down payment and paid off at the sale.",
    fit: "Buyers who can open the line before listing and want cheaper access to equity than a bridge loan.",
    watch:
      "Most lenders will not open or fund a HELOC on a home that is already listed. Rates are typically variable. Terms vary by lender.",
  },
  {
    id: "recast",
    name: "Mortgage recasting",
    how: "After the old home sells, you apply a lump sum to the new mortgage principal and the lender re-amortizes the payment at the same rate and term.",
    fit: "Buyers who close on the new home with a small down payment and expect a large check later.",
    watch:
      "Not all loan types or servicers allow recasting, minimum lump sums and fees apply, and the rate does not change. Confirm the recast policy in writing before you close.",
  },
  {
    id: "buy-before-you-sell",
    name: "Buy-before-you-sell services",
    how: "A company buys or guarantees your current home, or advances the equity, so you can make a non-contingent offer and move once.",
    fit: "Competitive markets where a sale contingency effectively disqualifies your offer.",
    watch:
      "Program fees, guaranteed-price haircuts, and eligibility limits differ widely. Compare total cost against the price you would likely get on the open market. Terms vary by provider and market.",
  },
  {
    id: "sale-contingency",
    name: "Sale contingency",
    how: "A clause making your purchase conditional on your current home selling by a stated date.",
    fit: "Balanced or slow markets, or when no financing tool is available and you cannot carry two payments.",
    watch:
      "In competitive markets a contingent offer usually loses to a clean one at the same price, and many sellers add a kick-out clause letting them keep marketing the home. Contract terms control.",
  },
];

export type CoordinationItem = {
  id: string;
  title: string;
  body: string;
  owner: string;
};

export const COORDINATION: CoordinationItem[] = [
  {
    id: "referral-handoff",
    title: "Referral-agent handoff",
    body: "Your listing agent in the old city refers you to an agent in the new one, usually under a written referral agreement. Ask both agents to introduce themselves to each other directly and to share your target dates, not just your contact information. A handoff that happens over email between the two agents is far more reliable than one where you relay every message.",
    owner: "You, with both agents",
  },
  {
    id: "closing-dates",
    title: "Closing-date communication",
    body: "Pick the purchase closing date first if the market lets you, then work the sale date backward. Give both closing agents each other's contact details the day both contracts are signed, and confirm funding cutoffs — a purchase cannot fund on wired proceeds that arrive after the closer's daily deadline.",
    owner: "Both closing agents, confirmed by you",
  },
  {
    id: "sale-tasks",
    title: "Sale-side tasks",
    body: "Pre-listing repairs, staging, photography, showing access after you have moved, buyer inspection and appraisal access, and the payoff statement on your existing mortgage.",
    owner: "Listing agent",
  },
  {
    id: "purchase-tasks",
    title: "Purchase-side tasks",
    body: "Neighborhood and commute research, offer strategy, inspection scheduling in a city you may not be standing in, and a walkthrough that someone can attend if you cannot.",
    owner: "Buyer's agent in the new city",
  },
  {
    id: "lender-tasks",
    title: "Lender tasks",
    body: "Pre-approval that accounts for the departing residence, any bridge or HELOC application, rate lock length matched to the real closing date, and re-verification of employment if you are changing jobs with the move.",
    owner: "Loan officer",
  },
  {
    id: "mover-tasks",
    title: "Mover and logistics tasks",
    body: "Long-distance quotes and a binding estimate, load and delivery windows (interstate delivery is a window, not a date), storage-in-transit if the dates gap, and valuation coverage.",
    owner: "You and the moving company",
  },
  {
    id: "school-tasks",
    title: "School and family transfer tasks",
    body: "Records request from the current school, enrollment paperwork and proof-of-residency rules in the new district, immunization requirements that differ by state, and IEP or 504 plan transfer if applicable.",
    owner: "You, with both school districts",
  },
];

export type HousingOption = {
  id: string;
  name: string;
  window: string;
  body: string;
  reduces: string;
};

export const TEMP_HOUSING: HousingOption[] = [
  {
    id: "rent-back",
    name: "Rent-back (post-occupancy agreement)",
    window: "Typically 30 to 60 days after closing",
    body: "You sell, then rent your former home back from the new owner for a set period at a daily rate, often roughly the buyer's daily carrying cost. It is a written agreement with a start and end date, an occupancy fee, insurance requirements, and a condition standard for handover. Many lenders limit owner-occupied buyers to 60 days, and some markets treat longer stays as a tenancy with tenant protections attached.",
    reduces: "Best when your sale closes first and your purchase closes weeks later — one move, no storage.",
  },
  {
    id: "escrow-holdback",
    name: "Escrow holdback",
    window: "Held until the agreed condition is met",
    body: "A portion of the seller's proceeds stays with the closing agent as security — for a rent-back, it covers damage or a failure to vacate on time; for repairs, it covers unfinished work. It is released when the condition is satisfied. The amount, release conditions, and who holds it are negotiated in the contract, not standardized.",
    reduces: "Lets a nervous buyer agree to a rent-back they would otherwise refuse.",
  },
  {
    id: "short-term-rental",
    name: "Short-term or corporate rental",
    window: "Weeks to a few months",
    body: "A furnished rental in the new city while you shop or wait to close. It removes the pressure to buy the first acceptable house and lets you learn neighborhoods before committing.",
    reduces: "Best for sell-first moves into an unfamiliar market — but budget for two moves and storage.",
  },
  {
    id: "storage",
    name: "Storage in transit",
    window: "Days to months",
    body: "The mover holds your shipment in a warehouse and delivers when you have keys. Simpler than self-storage because your goods stay under one bill of lading and one valuation policy; a second handling adds damage risk and cost.",
    reduces: "Bridges a gap of a few days to a few weeks between closings without a second full move.",
  },
];

export type ChecklistStage = {
  id: string;
  heading: string;
  when: string;
  items: string[];
};

export const RELOCATION_CHECKLIST: ChecklistStage[] = [
  {
    id: "stage-3-6-months",
    heading: "3 to 6 months out",
    when: "Early planning",
    items: [
      "Get a pre-approval that accounts for both homes, and ask the lender how the departing residence affects your qualification.",
      "Get a listing consultation and a realistic net-proceeds estimate for your current home.",
      "Choose your timing strategy — sell first, buy first, or simultaneous — and pick the financing tool that supports it.",
      "Ask your listing agent for a referral agent in the destination city and get the three of you on one email thread.",
      "Research neighborhoods, commutes, and school attendance boundaries; boundaries are not the same as city limits.",
      "Confirm relocation benefits with your employer in writing: what is covered, what is reimbursed, and what is taxable.",
      "Start decluttering — long-distance moves are priced by weight.",
    ],
  },
  {
    id: "stage-8-12-weeks",
    heading: "8 to 12 weeks out",
    when: "Locking in the plan",
    items: [
      "Collect at least three in-home or video survey quotes from licensed interstate movers and ask for binding estimates.",
      "Verify each mover's USDOT number and complaint history before signing anything.",
      "Complete pre-listing repairs, then schedule staging and photography.",
      "Request school records and the destination district's enrollment packet, including proof-of-residency and immunization rules.",
      "Apply for the bridge loan, HELOC, or buy-before-you-sell program if your strategy depends on one.",
      "Book the moving dates you want; peak season books out first.",
      "Notify your employer's payroll and HR of the expected address and state change date.",
    ],
  },
  {
    id: "stage-4-6-weeks",
    heading: "4 to 6 weeks out",
    when: "Contracts and logistics",
    items: [
      "List the home or accept the offer, then set the closing dates for both transactions and share them with both closers.",
      "Match your rate lock length to the real closing date, not the optimistic one.",
      "Schedule utility transfers for both addresses: electric, gas, water, sewer, trash, and internet.",
      "Arrange temporary housing, rent-back, or storage in transit if the dates do not line up.",
      "Order the mortgage payoff statement on the departing home and check its expiration date.",
      "Book travel for the move itself and for anyone who must attend a closing in person.",
      "Enroll the kids in the new school and confirm the start date and bus routing.",
    ],
  },
  {
    id: "stage-2-weeks",
    heading: "2 weeks out",
    when: "Final coordination",
    items: [
      "Confirm both closings in writing: date, time, location, funding cutoff, and remote or in-person signing.",
      "Verify wiring instructions by calling the closing agent at a number you looked up yourself — never from an email.",
      "File the USPS change of address and update banks, insurers, subscriptions, and your driver's license and registration timeline.",
      "Transfer or rewrite homeowners insurance so the new policy is effective on the purchase closing date.",
      "Confirm the mover's load date, delivery window, and inventory list.",
      "Refill prescriptions and request medical, dental, and veterinary records.",
      "Pack a first-night box and keep closing documents, IDs, and valuables with you, not on the truck.",
    ],
  },
  {
    id: "stage-moving-day",
    heading: "Moving day and the days around it",
    when: "Execution",
    items: [
      "Photograph every utility meter at both homes with a timestamp — this settles the vast majority of final-bill disputes.",
      "Walk the departing home with the inventory list and check every closet, attic, and crawlspace before the truck leaves.",
      "Do the buyer's final walkthrough or send your representative if you have already left.",
      "Leave keys, remotes, codes, manuals, and warranties where the contract says they go.",
      "Attend or complete the remote closing on the purchase and confirm funds were received and the deed recorded.",
      "Note the delivery window, not a delivery date — interstate shipments arrive inside a range.",
      "Check mail forwarding is live and change your address with your employer's payroll for state tax withholding.",
    ],
  },
];

export const RELOCATION_FAQS = [
  {
    id: "sell-first-or-buy-first",
    q: "Should I sell my home first or buy the new one first?",
    a: "Sell first if you need the equity to qualify or to fund the down payment, or if you are buying into a competitive market where a contingent offer will not be taken seriously. Buy first if you can carry both payments or have a bridge loan, HELOC, or buy-before-you-sell program in place, and you want to move only once. The deciding factor is almost never preference — it is whether your proceeds are needed to close, and whether your lender will approve you while still owning the departing residence.",
  },
  {
    id: "bridge-vs-contingent",
    q: "Is a bridge loan better than making a contingent offer?",
    a: "A bridge loan makes your offer non-contingent, which is usually what wins in a competitive market, but you pay interest and fees and take on the risk that your old home sells slowly. A contingent offer costs nothing up front but is often rejected outright when other buyers are clean, and sellers frequently attach a kick-out clause that lets them keep showing the home. In a balanced or slow market a contingency is reasonable; in a tight one it is often the reason your offer loses. Rates, fees, and availability vary by lender.",
  },
  {
    id: "same-day-closings",
    q: "Can I close on both homes the same day?",
    a: "Sometimes, and it is harder across two cities or states than within one. The sale has to fund and wire before the purchase can fund, and every closer has a daily wire cutoff. States also differ on whether keys transfer at signing or after recording. If you attempt it, have both closing agents speak directly, schedule the sale in the morning and the purchase in the afternoon where time zones allow, and keep a fallback plan for a one to three day gap — a rent-back, a hotel, or storage in transit.",
  },
  {
    id: "utility-transfer-timing",
    q: "When should I transfer utilities for both homes?",
    a: "Schedule the transfers about four to six weeks before closing and set the departing home's service to end the day after your sale closes, with the new home's service starting the day before your purchase closes. That one-day overlap on each end protects you from a funding delay and from arriving to a house with no power. Some providers need several business days of notice, and a few require an in-person appointment. Photograph every meter on the day service changes.",
  },
  {
    id: "when-to-book-movers",
    q: "How far in advance should I book long-distance movers?",
    a: "Get quotes 8 to 12 weeks out and book as soon as your closing dates are set, generally four to six weeks ahead. Summer, month-ends, and the days around the first of the month sell out first. Ask for a binding or not-to-exceed estimate based on an in-home or video survey rather than a phone guess, verify the mover's USDOT number, and confirm whether your quoted arrival is a delivery date or a delivery window.",
  },
];